import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Nav } from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Loader2, CheckCircle2, Building2, Link2, Copy, Check, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);
const emptyRef = () => ({ _uid: uid(), company_name: "", contact: "", email: "" });
const withUid = (r) => (r._uid ? r : { ...r, _uid: uid() });
const emptyCustomerInfo = { mailing_address: "", ap_contact_name: "", ap_email: "", requires_po: "", po_acquisition: "", tax_exempt: "" };

export default function CustomerReferenceForm() {
  const { user, refresh } = useAuth();
  const [references, setReferences] = useState([emptyRef(), emptyRef(), emptyRef()]);
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ company_name: "", contact_name: "", phone: "", account_number: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ ...emptyCustomerInfo });

  useEffect(() => {
    if (user) setProfile({ company_name: user.company_name || "", contact_name: user.contact_name || "", phone: user.phone || "", account_number: user.account_number || "" });
  }, [user]);

  const saveCustomerInfo = async () => {
    if (!profile.company_name.trim() || !profile.contact_name.trim()) { toast.error("Customer name and contact are required"); return; }
    setSavingProfile(true);
    try {
      await api.patch("/customer/profile", { ...profile, customer_info: customerInfo });
      await refresh();
      toast.success("Customer information saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    api
      .get("/customer/submission")
      .then(({ data }) => {
        if (data.submission) {
          const refs = (data.submission.references || []).map(withUid);
          while (refs.length < 3) refs.push(emptyRef());
          setReferences(refs);
          setSignature(data.submission.signature || "");
          setAgreed(!!data.submission.agreed_terms);
          setCustomerInfo({ ...emptyCustomerInfo, ...(data.submission.customer_info || {}) });
          setSubmitted(!!(data.submission.signature && data.submission.signature.trim()));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const updateRef = (i, field, val) => {
    setReferences((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  };

  const addRef = () => setReferences((prev) => [...prev, emptyRef()]);
  const removeRef = (i) => setReferences((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.patch("/customer/profile", { ...profile, customer_info: customerInfo });
      await api.post("/customer/submission", { references, signature, agreed_terms: agreed, customer_info: customerInfo });
      const { data } = await api.get("/customer/submission");
      if (data.submission) {
        const refs = (data.submission.references || []).map(withUid);
        while (refs.length < 3) refs.push(emptyRef());
        setReferences(refs);
      }
      setSubmitted(true);
      toast.success("Credit reference form submitted successfully");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F4F5]">
        <Nav />
        <div className="flex justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <Nav />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">Apply for a line of credit</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-[#0F172A]">
            Credit / Trade Reference Form
          </h1>
          <p className="mt-3 text-zinc-500 max-w-prose">
            Please provide your trade references below. Each reference should include the company name, a contact person,
            and their email address.
          </p>

          {submitted && (
            <div className="mt-6 flex items-center gap-3 bg-[#10B981]/10 border border-[#10B981]/30 p-4" data-testid="submitted-banner">
              <CheckCircle2 className="h-5 w-5 text-[#10B981]" />
              <p className="text-sm text-[#0F172A]">
                Your form has been submitted. You can update your references anytime below.
              </p>
            </div>
          )}

          {/* Customer Information Form (single consolidated section, matches PDF) */}
          <form onSubmit={handleSubmit} className="mt-8" data-testid="reference-form">
            <div className="bg-white border border-zinc-200 p-6 mb-8" data-testid="customer-info-section">
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="h-4 w-4 text-[#D4AF37]" />
                <h2 className="font-display font-bold text-[#0F172A]">Customer Information Form</h2>
              </div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-5">Please complete this section</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Customer Name" value={profile.company_name} onChange={(v) => setProfile({ ...profile, company_name: v })} testid="ci-customer-name" />
                <div className="sm:col-span-2">
                  <Field label="Mailing Address" value={customerInfo.mailing_address} onChange={(v) => setCustomerInfo({ ...customerInfo, mailing_address: v })} testid="ci-mailing-address" />
                </div>
                <Field label="Accounts Payable Contact Name" value={customerInfo.ap_contact_name} onChange={(v) => setCustomerInfo({ ...customerInfo, ap_contact_name: v })} testid="ci-ap-contact" />
                <Field label="Accounts Payable Email" type="email" value={customerInfo.ap_email} onChange={(v) => setCustomerInfo({ ...customerInfo, ap_email: v })} testid="ci-ap-email" />
                <Field label="Phone" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} testid="ci-phone" />
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">Email</label>
                  <div className="px-3 py-2.5 border border-zinc-200 bg-zinc-50 text-sm font-mono text-zinc-500 break-all" data-testid="ci-email" title="This is your login email and cannot be changed here">
                    {user?.email || "—"}
                  </div>
                </div>
                <YesNo label="Does your company require a PO#?" value={customerInfo.requires_po} onChange={(v) => setCustomerInfo({ ...customerInfo, requires_po: v })} testid="ci-requires-po" />
                <Field label="If so, how is this PO# acquired?" value={customerInfo.po_acquisition} onChange={(v) => setCustomerInfo({ ...customerInfo, po_acquisition: v })} testid="ci-po-acquisition" />
                <YesNo label="Is your company tax exempt?" value={customerInfo.tax_exempt} onChange={(v) => setCustomerInfo({ ...customerInfo, tax_exempt: v })} testid="ci-tax-exempt" />
              </div>
              {customerInfo.tax_exempt === "Yes" && <TaxExemptUpload user={user} refresh={refresh} />}
              <div className="mt-5 pt-5 border-t border-zinc-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Contact Name" value={profile.contact_name} onChange={(v) => setProfile({ ...profile, contact_name: v })} testid="ci-contact-name" />
                <Field label="Account Number" value={profile.account_number} onChange={(v) => setProfile({ ...profile, account_number: v })} testid="ci-account-number" />
              </div>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={saveCustomerInfo}
                  disabled={savingProfile}
                  data-testid="ci-save"
                  className="inline-flex items-center gap-2 bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
                >
                  {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save customer information"}
                </button>
              </div>
            </div>
            {/* References */}
            <div className="bg-white border border-zinc-200">
              <div className="flex items-center justify-between p-6 border-b border-zinc-200">
                <h2 className="font-display font-bold text-[#0F172A]">Credit / Trade References</h2>
                <span className="text-xs text-zinc-400 font-mono">{references.length} references</span>
              </div>
              <div className="divide-y divide-zinc-200">
                {references.map((ref, i) => (
                  <div key={ref._uid || i} className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_1fr_auto] gap-4 items-end">
                      <div className="hidden md:flex h-10 w-10 bg-[#0F172A] text-white items-center justify-center font-mono text-sm">
                        {i + 1}
                      </div>
                      <Field
                        label="Company Name"
                        value={ref.company_name}
                        onChange={(v) => updateRef(i, "company_name", v)}
                        testid={`ref-company-${i}`}
                      />
                      <Field
                        label="Contact"
                        value={ref.contact}
                        onChange={(v) => updateRef(i, "contact", v)}
                        testid={`ref-contact-${i}`}
                      />
                      <Field
                        label="Email"
                        type="email"
                        value={ref.email}
                        onChange={(v) => updateRef(i, "email", v)}
                        testid={`ref-email-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeRef(i)}
                        disabled={references.length <= 1}
                        data-testid={`ref-remove-${i}`}
                        className="h-10 w-10 flex items-center justify-center text-zinc-400 hover:text-[#EF4444] disabled:opacity-30 transition-colors"
                        title="Remove reference"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {ref.token && <ReferenceLinkPanel refObj={ref} index={i} />}
                  </div>
                ))}
              </div>
              <div className="p-6 border-t border-zinc-200">
                <button
                  type="button"
                  onClick={addRef}
                  data-testid="add-reference"
                  className="flex items-center gap-2 px-4 py-2.5 text-sm border border-[#0F172A] text-[#0F172A] hover:bg-zinc-100 transition-colors"
                >
                  <Plus className="h-4 w-4" /> Add another reference
                </button>
              </div>
            </div>

            {/* Authorization */}
            <div className="mt-8 bg-white border border-zinc-200 p-6 space-y-5">
              <h2 className="font-display font-bold text-[#0F172A]">Authorization</h2>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Customer hereby authorizes release of the above credit information and authorizes Peregrine Air Systems
                (PAS), Peregrine Additives &amp; Lubes LLC (PAL) / Peregrine Instrumentation from time to time to obtain
                Credit Reports.
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  data-testid="agree-terms"
                  className="mt-1 h-4 w-4 accent-[#D4AF37]"
                />
                <span className="text-sm text-zinc-700">
                  I agree to Peregrine's credit payment terms of <span className="font-medium text-[#0F172A]">Net 30 days</span> from the date of invoice.
                </span>
              </label>
              <div className="max-w-sm">
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Customer Signature (type full name)</label>
                <input
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  data-testid="signature"
                  className="w-full px-4 py-3 border border-zinc-300 bg-white outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
                  placeholder="Full legal name"
                />
              </div>
            </div>

            {error && <p className="mt-4 text-sm text-[#EF4444]" data-testid="form-error">{error}</p>}

            <div className="mt-8 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-form"
                className="flex items-center gap-2 bg-[#0F172A] text-white px-8 py-3.5 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : submitted ? "Update references" : "Submit form"}
              </button>
            </div>
          </form>
        </motion.div>
      </main>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
      />
    </div>
  );
}

function YesNo({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wide">{label}</label>
      <div className="flex items-center gap-4 h-[42px]">
        {["Yes", "No"].map((opt) => (
          <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm text-zinc-700">
            <input
              type="radio"
              name={testid}
              checked={value === opt}
              onChange={() => onChange(opt)}
              data-testid={`${testid}-${opt.toLowerCase()}`}
              className="h-4 w-4 accent-[#D4AF37]"
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );
}

function ProfileField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wide text-zinc-400 mb-1.5">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
      />
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-zinc-400">{label}</div>
      <div className={`text-[#0F172A] mt-0.5 ${mono ? "font-mono text-xs break-all" : "font-medium"}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function CopyLine({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] uppercase tracking-wide text-zinc-400 w-20 shrink-0">{label}</span>
      <code className="flex-1 font-mono text-xs bg-white border border-zinc-200 px-2 py-1.5 truncate">{value}</code>
      <button type="button" onClick={copy} data-testid={testid} className="p-1.5 text-zinc-400 hover:text-[#0F172A] transition-colors" title={`Copy ${label}`}>
        {copied ? <Check className="h-4 w-4 text-[#10B981]" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function ReferenceLinkPanel({ refObj, index }) {
  const link = `${window.location.origin}/reference/${refObj.token}`;
  const completed = refObj.credit_status === "completed";
  return (
    <div className="border border-zinc-200 bg-zinc-50 p-4 space-y-3" data-testid={`ref-link-panel-${index}`}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs uppercase tracking-wide text-zinc-500">
          <Link2 className="h-3.5 w-3.5 text-[#D4AF37]" /> Reference credit-check link
        </span>
        {completed ? (
          <span className="inline-flex items-center gap-1.5 text-[#10B981] text-xs font-medium">
            <span className="h-1.5 w-1.5 bg-[#10B981] rounded-full" /> Completed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-zinc-400 text-xs">
            <span className="h-1.5 w-1.5 bg-zinc-300 rounded-full" /> Awaiting response
          </span>
        )}
      </div>
      <CopyLine label="Link" value={link} testid={`ref-copy-link-${index}`} />
      <CopyLine label="Password" value={refObj.password} testid={`ref-copy-pass-${index}`} />
      <p className="text-xs text-zinc-400">
        Share this link and password with {refObj.company_name || "your reference"} so they can complete their credit check.
      </p>
    </div>
  );
}

function TaxExemptUpload({ user, refresh }) {
  const [uploading, setUploading] = useState(false);
  const current = user?.tax_exempt_file;
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/customer/tax-exempt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Tax exemption certificate uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="mt-4 border border-dashed border-[#D4AF37]/60 bg-[#D4AF37]/5 p-4" data-testid="tax-exempt-upload">
      <p className="text-sm text-[#0F172A] font-medium">Please provide a tax exemption certificate</p>
      {current && (
        <div className="mt-2 flex items-center gap-2 text-xs text-zinc-600" data-testid="tax-exempt-current">
          <FileText className="h-3.5 w-3.5 text-[#10B981]" />
          <span className="truncate">{current.filename || "Certificate on file"}</span>
          <Check className="h-3.5 w-3.5 text-[#10B981]" />
        </div>
      )}
      <label className="mt-3 inline-flex items-center gap-2 cursor-pointer border border-[#0F172A] text-[#0F172A] px-4 py-2 text-sm hover:bg-zinc-100 transition-colors">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {current ? "Replace certificate" : "Upload certificate"}
        <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx" className="hidden" onChange={onFile} disabled={uploading} data-testid="tax-exempt-file-input" />
      </label>
    </div>
  );
}
